import React, { useState } from 'react';
import { createProject, updateProject } from '../../api/projects';
import './Projects.css';

const numField = (p, k) => p?.[k] ?? '';

const KNOWN_FLAGS = [
  { name: 'Denmark',        emoji: '🇩🇰' },
  { name: 'Norway',         emoji: '🇳🇴' },
  { name: 'Sweden',         emoji: '🇸🇪' },
  { name: 'United Kingdom', emoji: '🇬🇧' },
  { name: 'Scotland',       emoji: '🏴󠁧󠁢󠁳󠁣󠁴󠁿' },
  { name: 'Ireland',        emoji: '🇮🇪' },
  { name: 'England',        emoji: '🏴󠁧󠁢󠁥󠁮󠁧󠁿' },
];

function NumField({ label, unit, value, onChange, step }) {
  return (
    <div className="form-field">
      <label>{label}{unit ? ` (${unit})` : ''}</label>
      <input type="text" inputMode="decimal" value={value} onChange={onChange} />
    </div>
  );
}

function ProjectForm({ project, onSaved, onCancel }) {
  const [form, setForm] = useState({
    name:          project?.name          || '',
    newbuildingNo: project?.newbuildingNo || '',
    shipName:      project?.shipName      || '',
    hullNumber:    project?.hullNumber    || '',
    client:        project?.client        || '',
    vesselType:    project?.vesselType    || '',
    flag:          project?.flag          || '',
    homePort:      project?.homePort      || '',
    imoNumber:     project?.imoNumber     || '',
    signalLetters: project?.signalLetters || '',
    keelDate:      project?.keelDate     ? project.keelDate.slice(0, 10)     : '',
    deliveryDate:  project?.deliveryDate ? project.deliveryDate.slice(0, 10) : '',
    ruleSet:       project?.ruleSet       || '',
    ruleEdition:   project?.ruleEdition   || '',
    classNotation: project?.classNotation || '',
    // Main dimensions
    loa_m:              numField(project, 'loa_m'),
    lpp_m:              numField(project, 'lpp_m'),
    breadth_m:          numField(project, 'breadth_m'),
    depthMainDeck_m:    numField(project, 'depthMainDeck_m'),
    depthShelterDeck_m: numField(project, 'depthShelterDeck_m'),
    draught_m:          numField(project, 'draught_m'),
    frameSpacing_mm:    numField(project, 'frameSpacing_mm'),
    crew:               numField(project, 'crew'),
    // Capacities
    fuelOil_m3:      numField(project, 'fuelOil_m3'),
    freshWater_m3:   numField(project, 'freshWater_m3'),
    forepeak_m3:     numField(project, 'forepeak_m3'),
    lubOil_m3:       numField(project, 'lubOil_m3'),
    hydraulicOil_m3: numField(project, 'hydraulicOil_m3'),
    urea_m3:         numField(project, 'urea_m3'),
    freshHold_m3:    numField(project, 'freshHold_m3'),
    rswTanks_m3:     numField(project, 'rswTanks_m3'),
    tonnage_gt:      numField(project, 'tonnage_gt'),
    note: project?.note || '',
  });
  const [error, setError]   = useState('');
  const [saving, setSaving] = useState(false);

  const set   = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const num   = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const toNum = (v) => {
    if (v === '' || v == null) return undefined;
    const n = parseFloat(String(v).replace(',', '.'));
    return isNaN(n) ? undefined : n;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return setError('Project name is required.');
    setError('');
    setSaving(true);
    try {
      const payload = {
        ...form,
        keelDate:           form.keelDate     || undefined,
        deliveryDate:       form.deliveryDate || undefined,
        loa_m:              toNum(form.loa_m),
        lpp_m:              toNum(form.lpp_m),
        breadth_m:          toNum(form.breadth_m),
        depthMainDeck_m:    toNum(form.depthMainDeck_m),
        depthShelterDeck_m: toNum(form.depthShelterDeck_m),
        draught_m:          toNum(form.draught_m),
        frameSpacing_mm:    toNum(form.frameSpacing_mm),
        crew:               toNum(form.crew),
        fuelOil_m3:         toNum(form.fuelOil_m3),
        freshWater_m3:      toNum(form.freshWater_m3),
        forepeak_m3:        toNum(form.forepeak_m3),
        lubOil_m3:          toNum(form.lubOil_m3),
        hydraulicOil_m3:    toNum(form.hydraulicOil_m3),
        urea_m3:            toNum(form.urea_m3),
        freshHold_m3:       toNum(form.freshHold_m3),
        rswTanks_m3:        toNum(form.rswTanks_m3),
        tonnage_gt:         toNum(form.tonnage_gt),
      };
      const res = project
        ? await updateProject(project._id, payload)
        : await createProject(payload);
      onSaved(res.data);
    } catch (err) {
      setError(err.response?.data?.msg || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="module-wrap">
      <form onSubmit={handleSubmit} style={{ display: 'contents' }}>
        <div className="form-wrap">
          <h2 className="form-title">{project ? 'Edit Project' : 'New Project'}</h2>

          <div className="form-section">
            <div className="form-section-title">Vessel Details</div>
            <div className="form-grid">
              <div className="form-field" style={{ gridColumn: '1 / -1' }}>
                <label>Project Name *</label>
                <input value={form.name} onChange={set('name')} required autoFocus />
              </div>
              <div className="form-field">
                <label>Ship Name</label>
                <input value={form.shipName} onChange={set('shipName')} />
              </div>
              <div className="form-field">
                <label>NB No.</label>
                <input value={form.newbuildingNo} onChange={set('newbuildingNo')} placeholder="e.g. 123" />
              </div>
              <div className="form-field">
                <label>Client</label>
                <input value={form.client} onChange={set('client')} />
              </div>
              <div className="form-field">
                <label>Vessel Type</label>
                <input value={form.vesselType} onChange={set('vesselType')} />
              </div>
              <div className="form-field">
                <label>Flag</label>
                {(() => {
                  const isKnown = !form.flag || KNOWN_FLAGS.some(f => f.name === form.flag);
                  return (
                    <>
                      <select
                        value={isKnown ? form.flag : 'OTHER'}
                        onChange={e => {
                          if (e.target.value === 'OTHER') setForm(f => ({ ...f, flag: f.flag || '' }));
                          else setForm(f => ({ ...f, flag: e.target.value }));
                        }}
                      >
                        <option value="">—</option>
                        {KNOWN_FLAGS.map(f => <option key={f.name} value={f.name}>{f.emoji} {f.name}</option>)}
                        <option value="OTHER">Other…</option>
                      </select>
                      {!isKnown && (
                        <input value={form.flag} onChange={set('flag')} placeholder="Country name…" style={{ marginTop: 4 }} />
                      )}
                    </>
                  );
                })()}
              </div>
              <div className="form-field">
                <label>Port</label>
                <input value={form.homePort} onChange={set('homePort')} />
              </div>
              <div className="form-field">
                <label>Keel Date</label>
                <input type="date" value={form.keelDate} onChange={set('keelDate')} />
              </div>
              <div className="form-field">
                <label>Delivery Date</label>
                <input type="date" value={form.deliveryDate} onChange={set('deliveryDate')} />
              </div>
              <div className="form-field">
                <label>IMO Number</label>
                <input value={form.imoNumber} onChange={set('imoNumber')} />
              </div>
              <div className="form-field">
                <label>Signal Letters</label>
                <input value={form.signalLetters} onChange={set('signalLetters')} />
              </div>
            </div>
          </div>

          <div className="form-section">
            <div className="form-section-title">Design</div>
            <div className="form-grid">
              <div className="form-field">
                <label>Rule Set</label>
                <input value={form.ruleSet} onChange={set('ruleSet')} />
              </div>
              <div className="form-field">
                <label>Rule Edition</label>
                <input value={form.ruleEdition} onChange={set('ruleEdition')} />
              </div>
              <div className="form-field" style={{ gridColumn: '1 / -1' }}>
                <label>Class Notation</label>
                <input value={form.classNotation} onChange={set('classNotation')} />
              </div>
            </div>
          </div>

          <div className="form-section">
            <div className="form-section-title">Main Dimensions</div>
            <div className="form-grid">
              <NumField label="Length overall"     unit="m"  value={form.loa_m}              onChange={num('loa_m')} />
              <NumField label="Length b/w PP"      unit="m"  value={form.lpp_m}              onChange={num('lpp_m')} />
              <NumField label="Breadth moulded"    unit="m"  value={form.breadth_m}          onChange={num('breadth_m')} />
              <NumField label="Depth main deck"    unit="m"  value={form.depthMainDeck_m}    onChange={num('depthMainDeck_m')} />
              <NumField label="Depth shelter deck" unit="m"  value={form.depthShelterDeck_m} onChange={num('depthShelterDeck_m')} />
              <NumField label="Scantling draught"  unit="m"  value={form.draught_m}          onChange={num('draught_m')} />
              <NumField label="Frame spacing"      unit="mm" value={form.frameSpacing_mm}    onChange={num('frameSpacing_mm')} />
              <NumField label="Crew"                         value={form.crew}               onChange={num('crew')} />
            </div>
          </div>

          <div className="form-section">
            <div className="form-section-title">Capacities</div>
            <div className="form-grid">
              <NumField label="Fuel oil"              unit="m³" value={form.fuelOil_m3}      onChange={num('fuelOil_m3')} />
              <NumField label="Fresh water"           unit="m³" value={form.freshWater_m3}   onChange={num('freshWater_m3')} />
              <NumField label="Forepeak"              unit="m³" value={form.forepeak_m3}     onChange={num('forepeak_m3')} />
              <NumField label="Lubricating oil"       unit="m³" value={form.lubOil_m3}       onChange={num('lubOil_m3')} />
              <NumField label="Hydraulic oil"         unit="m³" value={form.hydraulicOil_m3} onChange={num('hydraulicOil_m3')} />
              <NumField label="Urea"                  unit="m³" value={form.urea_m3}         onChange={num('urea_m3')} />
              <NumField label="Fresh hold"            unit="m³" value={form.freshHold_m3}    onChange={num('freshHold_m3')} />
              <NumField label="RSW tanks"             unit="m³" value={form.rswTanks_m3}     onChange={num('rswTanks_m3')} />
              <NumField label="Tonnage"               unit="GT" value={form.tonnage_gt}      onChange={num('tonnage_gt')} />
            </div>
          </div>

          <div className="form-section">
            <div className="form-grid">
              <div className="form-field" style={{ gridColumn: '1 / -1' }}>
                <label>Note</label>
                <textarea value={form.note} onChange={set('note')} />
              </div>
            </div>
          </div>

          {error && <p className="form-error">{error}</p>}
        </div>
        <div className="form-actions">
          <button className="btn-primary" type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
          <button className="btn-secondary" type="button" onClick={onCancel}>Cancel</button>
        </div>
      </form>
    </div>
  );
}

export default ProjectForm;
